import { WarningOutlined } from '@ant-design/icons';
import { Row, Col, Radio, Button, Select } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import { Form, SubmitButton, Input } from 'formik-antd';
import { getWorkspace } from 'pages/LoginPage/thunk';
import { getMedicalInstitution } from 'pages/Register/RegisterFirstStep/thunk';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @ts-ignore
import zipcodeJa from 'zipcode-ja';
import { ModalWarningLeavePage } from 'components';
import useScrollTop from '../../../hooks/useScrollTop';
import { RootState } from '../../../types';
import { RegisterMedical, t_medical_institution } from '../../../types/medicalInstitution';
import { newMedical } from '../slices';
import { getDataStoreFieldMedicals } from '../thunk';
import schema from './schema';
import Wrap from './styles';

const { Option } = Select;
const { TextArea } = Input;

type Props = {
  nextStep: () => void;
};

const MedicalRegisterFirstStep: FC<Props> = ({ nextStep }) => {
  const { currentApplicationId } = useSelector((state: RootState) => state.workSpaces);
  const { dataStoreMedicalFields } = useSelector((state: RootState) => state.medicalRegister);
  const { medicalInstitution } = useSelector((state: RootState) => state.medicalRegister);
  const [option, setOption] = useState<string>(medicalInstitution.billable_month || '3');
  const [radio, setRadio] = useState<string>(medicalInstitution.payment_method || '');
  const [zipCodeLeft, setZipCodeLeft] = useState<string>(medicalInstitution.facility_zip?.slice(0, 3) || '');
  const [zipCodeRight, setZipCodeRight] = useState<string>(medicalInstitution.facility_zip?.slice(4, 8) || '');

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getWorkspace());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getMedicalInstitution());
  }, [dispatch]);

  useEffect(() => {
    currentApplicationId &&
      dispatch(
        getDataStoreFieldMedicals({
          appId: currentApplicationId,
          dataStoreId: 't_medical_institution',
        }),
      );
  }, [dispatch, currentApplicationId]);

  const paymentMethod = useMemo(() => {
    return (
      dataStoreMedicalFields?.find(([_key, value]: [string, any]) => value.display_id === 'payment_method')[1]
        .options || []
    );
  }, [dataStoreMedicalFields]);

  const changeRadio = (e: any) => {
    setRadio(e.target.value);
  };

  const handleZipCode = () => {
    const zipCode: string = `${zipCodeLeft}${zipCodeRight}`;
    const address = zipcodeJa[zipCode]?.address;
    const result = !address ? '' : address.join(' ');
    return result;
  };

  useScrollTop();
  const initialValues = {
    facility_name: medicalInstitution.facility_name || '',
    facility_zip: medicalInstitution.facility_zip || '',
    facility_address1: medicalInstitution.facility_address1 || '',
    facility_address2: medicalInstitution.facility_address2 || '',
    facility_tel: medicalInstitution.facility_tel || '',
    cpap_unit_price: medicalInstitution.cpap_unit_price || undefined,
    cpap_unit_sp_price: medicalInstitution.cpap_unit_sp_price || undefined,
    psg_unit_price: medicalInstitution.psg_unit_price || undefined,
    simplicity_unit_price: medicalInstitution.simplicity_unit_price || undefined,
    payment_method: radio,
    billable_month: option,
    account_name: medicalInstitution.account_name || '',
    remark_for_remittance: medicalInstitution.remark_for_remittance || '',
    in_charge_of_sales: medicalInstitution.in_charge_of_sales || '',
    facility_name_for_invoice: medicalInstitution.facility_name_for_invoice || '',
    facility_address_for_invoice: medicalInstitution.facility_address_for_invoice || '',
  };

  const handleSubmit = (values: any, form: FormikHelpers<RegisterMedical>) => {
    const facility_zip: string = zipCodeLeft && zipCodeRight ? `${zipCodeLeft}-${zipCodeRight}` : '';

    const medicalInformation: t_medical_institution = {
      facility_zip: facility_zip,
      facility_name: values.facility_name,
      facility_address1: values.facility_address1,
      facility_address2: values.facility_address2,
      facility_name_for_invoice: values.facility_name_for_invoice.trim(),
      facility_address_for_invoice: values.facility_address_for_invoice.trim(),
      facility_tel: values.facility_tel,
      payment_method: radio,
      cpap_unit_price: +values.cpap_unit_price,
      cpap_unit_sp_price: +values.cpap_unit_sp_price,
      psg_unit_price: +values.psg_unit_price,
      simplicity_unit_price: +values.simplicity_unit_price,
      billable_month: option,
      account_name: values.account_name,
      remark_for_remittance: values.remark_for_remittance,
      in_charge_of_sales: values.in_charge_of_sales,
    };
    form.setSubmitting(false);
    dispatch(newMedical({ ...medicalInformation }));
    nextStep();
  };
  return (
    <Wrap>
      <div className="section">
        <span className="smallBoldFont ※">※</span>
        <span className="standardFont">は必須項目</span>
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={schema}
          render={({ setFieldValue, isValid }) => (
            <Form autoComplete={'' + Math.random().toString()}>
              <div className="input-form">
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <div className="title-input">
                      <span className="standardBoldFont">医療機関名</span>
                      <span className="※ standardFont">※</span>
                    </div>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="facility_name">
                      <Input name="facility_name" autoComplete={`${Math.random()}`} size="large" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5} />
                  <Col span={15}>
                    <Row>
                      <label className="standardFont mb-7 bold">請求書出力用 改行位置設定</label>
                    </Row>
                    <Form.Item name="facility_name_for_invoice">
                      <TextArea name="facility_name_for_invoice" rows={4} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}></Col>
                  <Col style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }} span={15}>
                    <Col span={24}>
                      <div>
                        <label className="standardFont mb-7" htmlFor="post-office">
                          郵便番号
                        </label>
                      </div>
                    </Col>
                    <Form.Item name="codeLeft">
                      <Input
                        style={{ width: '92px' }}
                        id="post-office"
                        placeholder="例：576"
                        value={zipCodeLeft}
                        onChange={(e) => setZipCodeLeft(e.target.value)}
                        size="large"
                        name="codeLeft"
                        maxLength={3}
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                    <span style={{ margin: '0px 5px' }}> - </span>
                    <Form.Item name="codeRight">
                      <Input
                        style={{ width: '127px' }}
                        placeholder="例：0033"
                        value={zipCodeRight}
                        onChange={(e) => setZipCodeRight(e.target.value)}
                        size="large"
                        name="codeRight"
                        maxLength={4}
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                    <Button
                      onClick={() => {
                        setFieldValue('facility_address1', handleZipCode());
                      }}
                      className="btn-search btnDefault"
                    >
                      住所検索
                    </Button>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <div className="title-input">
                      <span className="standardBoldFont">所在地</span>
                    </div>
                  </Col>
                  <Col span={15}>
                    <Row>
                      <label className="standardFont mb-7" htmlFor="location">
                        都道府県/市区町村/番地
                      </label>
                    </Row>
                    <Form.Item name="facility_address1">
                      <Input
                        id="location"
                        name="facility_address1"
                        placeholder="例：大阪府 交野市 5-6-4"
                        size="large"
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}></Col>
                  <Col span={15}>
                    <Row>
                      <label className="standardFont mb-7" htmlFor="address">
                        マンション・ビル・建物
                      </label>
                    </Row>
                    <Form.Item name="facility_address2">
                      <Input
                        id="address"
                        name="facility_address2"
                        placeholder="例：スリープビル101号"
                        size="large"
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5} />
                  <Col span={15}>
                    <Row>
                      <label className="standardFont mb-7 bold">
                        請求書出力用 改行位置設定 （都道府県/市区町村/番地 マンション・ビル・建物）
                      </label>
                    </Row>
                    <Form.Item name="facility_address_for_invoice">
                      <TextArea name="facility_address_for_invoice" rows={4} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">電話番号</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="facility_tel">
                      <Input
                        name="facility_tel"
                        placeholder="例：072-819-5553"
                        size="large"
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align mt-28 mb-29">
                  <Col span={5}>
                    <span className="standardBoldFont">支払い方法</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="payment_method">
                      <Radio.Group value={radio}>
                        {paymentMethod.map((g: any, i: number) => (
                          <Radio key={i} onClick={(e) => changeRadio(e)} value={g.value}>
                            {g.value}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">CPAP単価</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="cpap_unit_price">
                      <Input className="pr" name="cpap_unit_price" size="large" autoComplete={`${Math.random()}`} />
                      <svg
                        style={{ position: 'absolute', top: '8px', left: '5px', width: '10px', height: '24px' }}
                        width="10"
                        height="11"
                        viewBox="0 0 10 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.474 10.916C5.39 10.412 5.362 10.048 5.362 9.348V7.976H7.42C8.022 7.976 8.4 8.004 8.876 8.088V7.038C8.4 7.122 8.036 7.15 7.42 7.15H5.362V6.492L5.922 5.652H7.42C8.036 5.652 8.414 5.68 8.89 5.764V4.714C8.414 4.798 8.05 4.826 7.42 4.826H6.44L8.596 1.494C8.974 0.92 9.268 0.514 9.464 0.276H7.952C7.854 0.598 7.686 0.906 7.28 1.564L4.844 5.512L2.394 1.564C1.946 0.863999 1.82 0.598 1.708 0.276H0.21C0.42 0.541999 0.644 0.863999 1.05 1.494L3.22 4.826H2.24C1.61 4.826 1.26 4.798 0.784 4.714V5.764C1.26 5.68 1.61 5.652 2.24 5.652H3.752L4.298 6.478V7.15H2.24C1.61 7.15 1.26 7.122 0.784 7.038V8.088C1.26 8.004 1.61 7.976 2.24 7.976H4.298V9.348C4.298 9.978 4.27 10.426 4.186 10.916H5.474Z"
                          fill="black"
                        />
                      </svg>
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">CPAP単価 特別料金</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="cpap_unit_sp_price">
                      <Input className="pr" name="cpap_unit_sp_price" size="large" autoComplete={`${Math.random()}`} />
                    </Form.Item>
                    <svg
                      style={{ position: 'absolute', top: '8px', left: '5px', width: '10px', height: '24px' }}
                      width="10"
                      height="11"
                      viewBox="0 0 10 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.474 10.916C5.39 10.412 5.362 10.048 5.362 9.348V7.976H7.42C8.022 7.976 8.4 8.004 8.876 8.088V7.038C8.4 7.122 8.036 7.15 7.42 7.15H5.362V6.492L5.922 5.652H7.42C8.036 5.652 8.414 5.68 8.89 5.764V4.714C8.414 4.798 8.05 4.826 7.42 4.826H6.44L8.596 1.494C8.974 0.92 9.268 0.514 9.464 0.276H7.952C7.854 0.598 7.686 0.906 7.28 1.564L4.844 5.512L2.394 1.564C1.946 0.863999 1.82 0.598 1.708 0.276H0.21C0.42 0.541999 0.644 0.863999 1.05 1.494L3.22 4.826H2.24C1.61 4.826 1.26 4.798 0.784 4.714V5.764C1.26 5.68 1.61 5.652 2.24 5.652H3.752L4.298 6.478V7.15H2.24C1.61 7.15 1.26 7.122 0.784 7.038V8.088C1.26 8.004 1.61 7.976 2.24 7.976H4.298V9.348C4.298 9.978 4.27 10.426 4.186 10.916H5.474Z"
                        fill="black"
                      />
                    </svg>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">出張PSG検査単価</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="psg_unit_price">
                      <Input className="pr" name="psg_unit_price" size="large" autoComplete={`${Math.random()}`} />
                      <svg
                        style={{ position: 'absolute', top: '8px', left: '5px', width: '10px', height: '24px' }}
                        width="10"
                        height="11"
                        viewBox="0 0 10 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.474 10.916C5.39 10.412 5.362 10.048 5.362 9.348V7.976H7.42C8.022 7.976 8.4 8.004 8.876 8.088V7.038C8.4 7.122 8.036 7.15 7.42 7.15H5.362V6.492L5.922 5.652H7.42C8.036 5.652 8.414 5.68 8.89 5.764V4.714C8.414 4.798 8.05 4.826 7.42 4.826H6.44L8.596 1.494C8.974 0.92 9.268 0.514 9.464 0.276H7.952C7.854 0.598 7.686 0.906 7.28 1.564L4.844 5.512L2.394 1.564C1.946 0.863999 1.82 0.598 1.708 0.276H0.21C0.42 0.541999 0.644 0.863999 1.05 1.494L3.22 4.826H2.24C1.61 4.826 1.26 4.798 0.784 4.714V5.764C1.26 5.68 1.61 5.652 2.24 5.652H3.752L4.298 6.478V7.15H2.24C1.61 7.15 1.26 7.122 0.784 7.038V8.088C1.26 8.004 1.61 7.976 2.24 7.976H4.298V9.348C4.298 9.978 4.27 10.426 4.186 10.916H5.474Z"
                          fill="black"
                        />
                      </svg>
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">簡易PSG検査単価</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="simplicity_unit_price">
                      <Input
                        className="pr"
                        name="simplicity_unit_price"
                        size="large"
                        autoComplete={`${Math.random()}`}
                      />
                      <svg
                        style={{ position: 'absolute', top: '8px', left: '5px', width: '10px', height: '24px' }}
                        width="10"
                        height="11"
                        viewBox="0 0 10 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.474 10.916C5.39 10.412 5.362 10.048 5.362 9.348V7.976H7.42C8.022 7.976 8.4 8.004 8.876 8.088V7.038C8.4 7.122 8.036 7.15 7.42 7.15H5.362V6.492L5.922 5.652H7.42C8.036 5.652 8.414 5.68 8.89 5.764V4.714C8.414 4.798 8.05 4.826 7.42 4.826H6.44L8.596 1.494C8.974 0.92 9.268 0.514 9.464 0.276H7.952C7.854 0.598 7.686 0.906 7.28 1.564L4.844 5.512L2.394 1.564C1.946 0.863999 1.82 0.598 1.708 0.276H0.21C0.42 0.541999 0.644 0.863999 1.05 1.494L3.22 4.826H2.24C1.61 4.826 1.26 4.798 0.784 4.714V5.764C1.26 5.68 1.61 5.652 2.24 5.652H3.752L4.298 6.478V7.15H2.24C1.61 7.15 1.26 7.122 0.784 7.038V8.088C1.26 8.004 1.61 7.976 2.24 7.976H4.298V9.348C4.298 9.978 4.27 10.426 4.186 10.916H5.474Z"
                          fill="black"
                        />
                      </svg>
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">請求可能未受診月数</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="payment_method">
                      <Select size="large" onChange={(e) => setOption(e)} value={option} style={{ width: '100px' }}>
                        {listBill.map((g: string, i: number) => (
                          <Option key={i} value={g}>
                            {g}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">入金名</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="account_name">
                      <Input name="account_name" size={'large'} autoComplete={`${Math.random()}`} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">入金リスト振込備考</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="remark_for_remittance">
                      <Input name="remark_for_remittance" size={'large'} autoComplete={`${Math.random()}`} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="pl-5 flex-align">
                  <Col span={5}>
                    <span className="standardBoldFont">営業担当</span>
                  </Col>
                  <Col span={15}>
                    <Form.Item name="in_charge_of_sales">
                      <Input name="in_charge_of_sales" size={'large'} autoComplete={`${Math.random()}`} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
              {!isValid && (
                <div style={{ marginTop: '0.5rem' }} className="error">
                  <WarningOutlined />
                  <p>入力内容に不備がございます。エラーが表示されている項目をご確認ください。</p>
                </div>
              )}
              <div className="submit">
                <SubmitButton className="btn btnDefault">登録内容を確認する</SubmitButton>
              </div>
            </Form>
          )}
        />
      </div>
      <ModalWarningLeavePage />
    </Wrap>
  );
};

const listBill: string[] = ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export default MedicalRegisterFirstStep;
